/* eslint-disable @typescript-eslint/naming-convention */
import 'reflect-metadata';
import { ColumnData, QueryParams } from '@ov-suite/helpers-shared';

export interface HasId {
  id: number | string;
}

export type Constructor<T = unknown> = new (...args: unknown[]) => T;

export interface GenericHierarchyStatus {
  id: number;
  name: string;
  color?: string;
}
export type ConstructorReturn<T extends HasId = HasId> = () => Constructor<T> | string;
export type ConstructorReturnArray<T extends HasId = HasId> = () => Constructor<T>[] | string[];

export interface GenericHierarchy<T extends GenericHierarchy<T> = { id: number | string; name?: string }> {
  id: number | string;
  name?: string;
  parent?: T;
  children?: T[];
  path?: string;
}

export interface FieldMetadata<T = unknown> {
  entity: Constructor<T> | null;
  name: string | null;
  plural: string | null;
  tableName: string | null;
  fields: FieldData[];
  format: (string | keyof T)[][];
  viewModeFormat?: (string | keyof T)[][];
  sideBarFormat: (string | keyof T)[][];
  table: ColumnData<T>[];
  mapDomainsToIds: boolean;
}

export interface ParentLookup {
  propertyKey: string;
  parentIndex: number;
  childIndex: number;
  format: string;
  foundIndicator: string;
  edited: boolean;
  concatenationProperties: ConcatenationPropertyKeys[];
  concatenationPropertyNames: string[];
  autoGenerated: boolean;
}

export interface Concatenation {
  propertyKeys: ConcatenationPropertyKeys[];
  format: string;
}

export interface ConcatenationPropertyKeys {
  propertyKey: string;
  innerPropertyKey?: string;
  subKey?: string;

  childIndex?: number; // exclusive to implementation
  parentIndex?: number; // exclusive to implementation
}

const defaultFieldMetadata: FieldMetadata = {
  entity: null,
  name: null,
  plural: null,
  tableName: null,
  fields: [],
  format: [],
  sideBarFormat: [],
  table: [],
  mapDomainsToIds: false,
};

export type FieldParams =
  | FieldParamsDefault
  | FieldParamsMap
  | FieldParamsNumber
  | FieldParamsDomainSelector
  | FieldParamsConstructor
  | (FieldParamsBase & FieldParamsCodePreview)
  | FieldParamsCustomerStagingTypeDialog

export type FieldParamsQuery = (key: string) => unknown;

export type FieldType = StandardType | 'number' | 'map' | 'hybrid-drop' | 'code-preview' | 'domain-selector' | 'customer-staging-type-dialog';

type StandardType =
  | 'string'
  | 'numbers'
  | 'title'
  | 'password'
  | 'image'
  | ConstructorReturn
  | ConstructorReturnArray
  | 'blank'
  | 'permission'
  | 'boolean'
  | 'json'
  | 'date'
  | 'date-time'
  | 'time'
  | 'button'
  | 'recurrence';

export interface FieldParamsDefault extends FieldParamsBase {
  type: StandardType;
}

export interface FieldParamsBase {
  // type: Omit<FieldType, 'map'>;
  bulkDependency?: string;
  bulkIgnore?: boolean;
  bulkColumnWidth?: number | null;
  exportExcludedKeys?: string[];
  exportUnique?: boolean;
  looselyCoupled?: boolean; // Set to true when doing cross api joins.
  /**
   * @deprecated use SelectionType instead
   */
  dropdown?: boolean;
  title?: string;
  placeholder?: string;
  tooltip?: string;
  errorMessage?: string; // NYI
  validatorInjectionKey?: string;
  generator?: ((data: unknown, dataQuery: FieldParamsQuery) => unknown) | null;
  action?: (data: CompiledFieldData[][]) => void;
  hidden?: (data: CompiledFieldData[]) => boolean;
  classes?: string[]; // NYI
  labelClasses?: string[]; // NYI

  sidebar?: boolean; // false // Main page or sidebar item

  required?: boolean; // false
  generated?: boolean; // false // Not inputted at creation
  readonly?: boolean; // false // Not editable during update
  unnecessary?: boolean; // false // Does not get returned on Queries
  editable?: boolean;
  idOnly?: boolean;
  concatenation?: Concatenation | null;
  search?: Record<string, QueryParams[]>;
  filter?: Record<string, QueryParams[]>;
  query?: Record<string, QueryParams[]>;
}

interface FieldParamsCodePreview {
  type: 'code-preview';
  unnecessary?: boolean;
  codeKey: string;
}

interface FieldParamsDomainSelector extends FieldParamsBase {
  type: 'domain-selector';
  isAuthenticationService?: boolean; // Default False
}

interface FieldParamsMap extends FieldParamsBase {
  type: 'map';
  // coordinatesKey: string;
  // addressKey: string;
}

interface FieldParamsCustomerStagingTypeDialog extends FieldParamsBase {
  type: 'customer-staging-type-dialog';
  templateRef: string;
}

export const fieldParamsDefaults: Required<FieldParams> = {
  type: 'string',
  bulkDependency: '',
  bulkIgnore: false,
  bulkColumnWidth: null,
  exportExcludedKeys: [],
  exportUnique: false,
  looselyCoupled: false,
  dropdown: false,
  title: '',
  placeholder: '',
  tooltip: '',
  errorMessage: '',
  validatorInjectionKey: '',
  generator: null,
  action: () => {},
  hidden: () => false,
  classes: [],
  labelClasses: [],
  sidebar: false,
  required: false,
  generated: false,
  readonly: false,
  unnecessary: false,
  idOnly: false,
  concatenation: null,
  editable: true,
  search: {},
  filter: {},
  query: {},
};

export function isFieldParamsConstructor(field: FieldParams): field is FieldParamsConstructor {
  return typeof field.type === 'function';
}

export interface FieldParamsConstructor extends FieldParamsBase {
  type: ConstructorReturn | ConstructorReturnArray;
  subType?: ConstructorReturn | ConstructorReturnArray | null; // used when withQuantity = true;
  selectionType?: 'simple' | 'single' | 'multiple'; // Simple is default dropdown, others are tree select tools
  withQuantity?: boolean;
  quantityKey?: string;
  nameColumnLabel?: string;
  nameColumnKey?: string | string[];
  nameColumnOverride?: ((object: unknown) => string) | null;
  flat?: boolean;
  keys?: string[]; //  get View properties
  dropdownKeys?: string[]; //  used to specify names in the simple dropdown
  dropdownLimit?: number;
}

export const fieldParamsConstructorDefaults: Required<FieldParamsConstructor> = {
  ...fieldParamsDefaults,
  type: () =>
    class Default {
      id: number;
    },
  subType: null,
  selectionType: 'simple',
  withQuantity: false,
  quantityKey: '',
  nameColumnLabel: 'Name',
  nameColumnKey: 'name',
  nameColumnOverride: null,
  flat: false,
  keys: [],
  dropdownKeys: ['name'],
  dropdownLimit: 100,
};

interface FieldParamsNumber extends FieldParamsBase {
  type: 'number';
  min?: string | number | null; // NYI
  max?: string | number | null; // NYI
  step?: string | number;
}

export const fieldParamsNumberDefaults: Required<FieldParamsNumber> = {
  ...fieldParamsDefaults,
  type: 'number',
  min: null,
  max: null,
  step: '1',
};

export type CompiledFieldData<T = unknown> = FieldData & {
  value: T;
  currentErrorMessage?: string;
  danger?: boolean;
};

export type FieldData = Required<FieldParams> & { propertyKey: string; isRelational?: boolean };

export function getFieldMetadata<T>(constructor: Constructor<T>): FieldMetadata<T> {
  return Reflect.getMetadata('OVEntity', constructor) ?? defaultFieldMetadata;
}

export function getFieldMetadataByType<T extends HasId>(type: ConstructorReturn<T> | ConstructorReturnArray<T>): FieldMetadata<T> {
  const value = type();
  let constructor: Constructor<T> | string;
  if (Array.isArray(value)) {
    [constructor] = value;
  } else {
    constructor = value;
  }
  if (typeof constructor === 'string') {
    return getFieldMetadata(getOVEntities()[constructor] as Constructor<T>);
  }
  return getFieldMetadata(constructor);
}

export function getCompiledFieldMetadata<T>(
  constructor: Constructor<T>,
  formatField: 'format' | 'viewModeFormat' = 'format',
): CompiledFieldData[][] {
  const allMetadata: FieldMetadata<T> = getFieldMetadata(constructor);
  return allMetadata[formatField].map(row =>
    row.map(item => {
      if (item.toString().startsWith('#')) {
        return {
          ...getFieldParamsWithDefaults({
            title: item.toString().slice(1),
            type: 'title',
          }),
          propertyKey: item.toString(),
          value: null,
        };
      }

      const found = allMetadata.fields.find(field => field.propertyKey === item);

      if (found) {
        return {
          ...found,
          value: null,
          danger: false,
        };
      }
      return {
        ...getFieldParamsWithDefaults({ type: 'blank' }),
        propertyKey: item.toString(),
        value: null,
      };
    }),
  );
}
export function getCompiledSidebarFieldMetadata<T>(constructor: Constructor<T>): CompiledFieldData[][] {
  const allMetadata: FieldMetadata<T> = getFieldMetadata(constructor);
  return allMetadata.sideBarFormat
    ? allMetadata.sideBarFormat.map(row =>
        row.map(item => {
          if (item.toString().startsWith('#')) {
            return {
              ...getFieldParamsWithDefaults({
                title: item.toString().slice(1),
                type: 'title',
              }),
              propertyKey: item.toString(),
              value: null,
            };
          }

          const found = allMetadata.fields.find(field => field.propertyKey === item);

          if (found) {
            return {
              ...found,
              value: null,
              danger: false,
            };
          }
          return {
            ...getFieldParamsWithDefaults({ type: 'blank' }),
            propertyKey: item.toString(),
            value: null,
          };
        }),
      )
    : [];
}

interface OVEntityParams {
  name: string;
  plural?: string;
  tableName?: string;
  // Should be used on all models relating to Authentication Service
  mapDomainsToIds?: boolean;
}

// Decorators
export function OVEntity<T>(params: OVEntityParams): ClassDecorator;
export function OVEntity<T>(name: string, plural?: string): ClassDecorator;
export function OVEntity<T>(...parameters: [OVEntityParams] | [string, string?]) {
  return <U = unknown>(constructor: U): U | void => {
    let name = '';
    let plural: string;
    let tableName: string;
    let mapDomainsToIds: boolean = false;
    if (typeof parameters[0] === 'string') {
      [name, plural = `${name}s`, tableName = name.toLowerCase()] = parameters;
    } else {
      [{ name, plural = `${name}s`, tableName = name.toLowerCase(), mapDomainsToIds }] = parameters;
    }
    console.assert(!!name, 'Name Missing on Model', { constructor });

    const allMetadata: FieldMetadata<T> = Reflect.getMetadata('OVEntity', constructor) ?? defaultFieldMetadata;
    const allEntities: Record<string, U> = Reflect.getMetadata('entities', OVEntity) ?? {};

    allMetadata.entity = constructor as unknown as Constructor<T>;
    allMetadata.name = name;
    allMetadata.plural = plural;
    allMetadata.tableName = tableName;
    allMetadata.mapDomainsToIds = mapDomainsToIds;
    allEntities[name] = constructor;

    Reflect.defineMetadata('OVEntity', allMetadata, constructor);
    Reflect.defineMetadata('entities', allEntities, OVEntity);
  };
}

export function getOVEntities(): Record<string, Constructor<HasId>> {
  return Reflect.getMetadata('entities', OVEntity) ?? {};
}

export function OVForm<T>(params: (string | keyof T)[][], viewModeParams?: (string | keyof T)[][]): ClassDecorator {
  return <U = unknown>(constructor: U): U | void => {
    const allMetadata: FieldMetadata<T> = Reflect.getMetadata('OVEntity', constructor) ?? defaultFieldMetadata;

    allMetadata.format = params;
    allMetadata.viewModeFormat = viewModeParams;

    Reflect.defineMetadata('OVEntity', allMetadata, constructor);
  };
}
export function OVSidebar<T>(params: (string | keyof T)[][]): ClassDecorator {
  return <U = unknown>(constructor: U): U | void => {
    const allMetadata: FieldMetadata<T> = Reflect.getMetadata('OVEntity', constructor) ?? defaultFieldMetadata;

    allMetadata.sideBarFormat = params;

    Reflect.defineMetadata('OVEntity', allMetadata, constructor);
  };
}

export interface OVSearchParams<T> {
  searchKeys: (string | keyof T)[];
}

export interface OVSearchData<T> {
  entity: Constructor<T>;
  searchKeys: (string | keyof T)[];
}

export function OVSearch<T = unknown>(params: OVSearchParams<T>): ClassDecorator {
  return <U = Constructor<T>>(constructor: U): U | void => {
    const data: OVSearchData<T> = {
      ...params,
      entity: constructor as unknown as Constructor<T>,
    };
    Reflect.defineMetadata('OVSearch', data, constructor);
  };
}

export function getSearchMetadata<T>(constructor: Constructor<T>): OVSearchData<T> {
  return Reflect.getMetadata('OVSearch', constructor);
}

export function OVTable<T = unknown>(params: ColumnData<T>[]): ClassDecorator {
  return <U = Constructor<T>>(constructor: U): U | void => {
    const allMetadata: FieldMetadata<T> = Reflect.getMetadata('OVEntity', constructor) ?? defaultFieldMetadata;

    const idList: string[] = [];

    allMetadata.table = params.map(col => {
      console.assert(!!col.title || !!col.id, 'Columns without Titles must have an ID set', { constructor, col });
      let id = col.id ?? col.title?.replace(/\s/gm, '_').toLowerCase() ?? '';

      if (id === 'status') {
        id = 'status_custom';
      }
      if (id === 'fast_actions') {
        id = 'fast_actions_custom';
      }
      console.assert(!idList.includes(id), 'Table does not have unique ID', {
        constructor,
        id,
      });

      idList.push(id);

      let orderKey: string;

      if (col.type === 'buttons' || col.type === 'button-bar' || col.type === 'other' || col.type === 'column' || col.type === 'pills') {
        console.assert(!!col.keys, `Table Data of type 'other' or 'buttons' or 'column' or 'pills' must have 'keys' declared`, constructor);
        orderKey = col.orderKey ?? col.keys?.length ? col.keys[0] : '';
      } else {
        orderKey = col.orderKey ?? (col.key as string);
      }

      return {
        ...col,
        id,
        orderKey,
      };
    });

    Reflect.defineMetadata('OVEntity', allMetadata, constructor);
  };
}
export function OVField(params: FieldParams) {
  return (target: Object, propertyKey: string) => {
    const { constructor } = target;
    const allMetadata: FieldMetadata = Reflect.getMetadata('OVEntity', constructor) ?? { fields: [], format: [], table: [] };

    const data: FieldData = {
      ...getFieldParamsWithDefaults(params),
      propertyKey,
      isRelational: typeof params.type !== 'string',
    };

    allMetadata.fields.push(data);

    Reflect.defineMetadata('OVEntity', allMetadata, constructor);
  };
}

export function getFieldParamsWithDefaults(params: FieldParams): Required<FieldParams> {
  if (params.type === 'number') {
    return mergeDefaults(params, fieldParamsNumberDefaults);
  }
  if (typeof params.type === 'function' && typeof params.type() === 'function') {
    return mergeDefaults(params, fieldParamsConstructorDefaults);
  }
  return mergeDefaults(params, fieldParamsDefaults);
}

function mergeDefaults(params: FieldParams, defaults: Required<FieldParams>): Required<FieldParams> {
  return { ...defaults, ...params } as Required<FieldParams>;
}

export function getTypeMetadata<T extends HasId = HasId>(
  type: ConstructorReturn<T> | ConstructorReturnArray<T>,
): { metadata: FieldMetadata<T>; entity: Constructor<T> } {
  const value = type();
  const [entityOrString] = Array.isArray(value) ? value : [value];
  if (typeof entityOrString === 'string') {
    const allEntities = getOVEntities();
    const entity = allEntities[entityOrString] as Constructor<T>;
    if (entity) {
      return { entity, metadata: getFieldMetadata(entity) };
    }
    throw new Error(`entity '${entityOrString}' not found`);
  } else {
    return { entity: entityOrString, metadata: getFieldMetadata(entityOrString) };
  }
}
